'use client';

import { c as _c } from "react/compiler-runtime";
import AuthWidgetContext from '@shared/utils/context/AuthWidgetContext';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import useFollowCounts from '@shared/utils/hooks/useFollowCounts';
import useLocalPreference from '@shared/utils/hooks/useLocalPreference';
import useUser from '@shared/utils/hooks/useUser';
import { useQuery } from '@tanstack/react-query';
import { Lightbulb, Plus, Users } from 'lucide-react';
import Head from 'next/head';
import { useContext, useState } from 'react';
import RunningJobs from '../../components/common/RunningJobs';
import RunningJobsIcon from '../../components/common/RunningJobsIcon';
import Posts from '../../components/posts/Posts';
import InlineProfileCard from '../../components/profile/InlineProfileCard';
import NewSpace from '../../components/spaces/NewSpace';
import { MySpacesSidebar, SuggestedSpacesSidebar } from '../../components/spaces/SpacesListSidebar';
import { useRunners } from '../../hooks/useRunners';
import AppLayout from '../../layouts/AppLayout/AppLayout';
import { client } from '../../utils/trpc';
const HomeLayout = () => {
  const $ = _c(26);
  const {
    localPreferenceState
  } = useLocalPreference();
  const {
    openAuthContinueModal
  } = useContext(AuthWidgetContext);
  const {
    currentUser
  } = useUser();
  const {
    followingCount
  } = useFollowCounts(client);
  const {
    sessions,
    runners
  } = useRunners();
  const hasRunnerActivity = sessions.length > 0 || runners.length > 0;
  const [newSpaceModalOpen, setNewSpaceModalOpen] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey: ["popularAccounts"],
      queryFn: _temp
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    isLoading,
    isError: error,
    data
  } = useQuery(t0);
  let t1;
  if ($[1] !== hasRunnerActivity) {
    t1 = hasRunnerActivity ? [{
      icon: <RunningJobsIcon />,
      text: "Running jobs",
      dataId: "running-jobs",
      component: <RunningJobs />
    }] : [];
    $[1] = hasRunnerActivity;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== currentUser.isAuthenticated) {
    t2 = currentUser.isAuthenticated ? [{
      icon: <Users />,
      text: "Your Spaces",
      dataId: "your-spaces",
      action: <button onClick={() => setNewSpaceModalOpen(true)} title="Add new space"><Plus size={18} /></button>,
      component: <MySpacesSidebar onNewSpace={() => setNewSpaceModalOpen(true)} />
    }] : [];
    $[3] = currentUser.isAuthenticated;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Lightbulb />;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      icon: t3,
      text: "Suggested",
      dataId: "suggested",
      component: <SuggestedSpacesSidebar />
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t1 || $[8] !== t2) {
    t5 = {
      items: [...t1, ...t2, t4]
    };
    $[7] = t1;
    $[8] = t2;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== currentUser.isAuthenticated || $[11] !== data || $[12] !== error || $[13] !== followingCount || $[14] !== isLoading || $[15] !== localPreferenceState.postModeType || $[16] !== openAuthContinueModal) {
    t6 = localPreferenceState.postModeType && <div><Head><title>{`${localPreferenceState.postModeType[0].toUpperCase() + localPreferenceState.postModeType.slice(1).toLowerCase()} - Factiii`}</title><meta name="description" content="At Factiii, our mission is to deliver reliable, unbiased information that stems from primary sources. We're passionate about embracing truth beyond politics and uniting in support of facts, no matter our personal opinions." /><meta name="keywords" content={`factiii, ${process.env.NEXT_PUBLIC_SITE_URL}, research platform`} /><meta property="og:title" content="Factiii - The World's First Research Platform" /><meta property="og:description" content="At Factiii, our mission is to deliver reliable, unbiased information that stems from primary sources. We're passionate about embracing truth beyond politics and uniting in support of facts, no matter our personal opinions." /></Head>{followingCount === 0 && localPreferenceState.postModeType === "home" && <div className="flex flex-col">{!isLoading && !error && data && <div className="flex flex-col gap-y-2"><h1 className="text-lg font-semibold">Connect with Popular Users</h1><p className="font-thin text-textSecondary/70">Discover and follow users who are making waves in the community. Stay updated with their latest activities and posts.</p>{data.users.map(aUser => <InlineProfileCard key={aUser.id} showAuthModal={openAuthContinueModal} loggedIn={currentUser.isAuthenticated} title="Connect with Popular Users" user={aUser} space={undefined} />)}</div>}{isLoading && <p className="text-center text-textSecondary/70">Loading results...</p>}{error && <p className="text-center text-destructive">An error occurred. Please try again later.</p>}</div>}<Posts params={{
        source: localPreferenceState.postModeType === "home" ? "home" : "explore"
      }} disableLink={false} isFullLength={false} /></div>;
    $[10] = currentUser.isAuthenticated;
    $[11] = data;
    $[12] = error;
    $[13] = followingCount;
    $[14] = isLoading;
    $[15] = localPreferenceState.postModeType;
    $[16] = openAuthContinueModal;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t6) {
    t7 = <div data-id="welcome">{t6}</div>;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== newSpaceModalOpen) {
    t8 = newSpaceModalOpen && <NewSpace closeHandler={() => setNewSpaceModalOpen(false)} />;
    $[20] = newSpaceModalOpen;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t5 || $[23] !== t7 || $[24] !== t8) {
    t9 = <AppLayout leftSidebar={t5} showPostRenderOptions={true}>{t7}{t8}</AppLayout>;
    $[22] = t5;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  return t9;
};
export default HomeLayout;
function _temp() {
  return unwrapFromResult(client.users.popular.query());
}